// src/services/VoiceRoomService.js
import useJwt from '@/auth/jwt/useJwt' // API çağrıları için
import AgoraRTC from 'agora-rtc-sdk-ng' // Agora için
import i18n from '@/libs/i18n'

// Fallback avatarı burada tanımlayabiliriz veya component'ten alabiliriz
const fallbackAvatar = require('@/assets/images/auth/female-avatar.png');

/**
 * Verilen kanal ID'si için yayıncıları (host ve speakers) alır ve işler.
 * @param {string|number} channelId - Kanalın ID'si.
 * @param {string} mainPublisherUuid - Ana yayıncının UUID'si.
 * @returns {Promise<{mainHost: object|null, speakers: Array<object>}>} İşlenmiş host ve speaker listesi. Hata durumunda hata fırlatır.
 */
export async function getInitialSpeakersData(channelId, mainPublisherUuid) {

  if (!channelId || !mainPublisherUuid) {
    console.error('[VoiceRoomService] channelId or mainPublisherUuid is missing.')
    throw new Error('Kanal bilgileri eksik.') // Component'in yakalaması için hata fırlat
  }

  try {
    const response = await useJwt.getVoiceRoomPublishers(channelId)

    console.log('users response', response)

    // Yanıtın ve body'nin geçerli bir array olduğunu kontrol et
    if (response && response.data && Array.isArray(response.data.body)) {
      const publishers = response.data.body

      let processedHost = null
      let processedSpeakers = []

      // Host'u bul ve işle (uuid ile bul, id olarak userId ata)
      const hostData = publishers.find(p => p.uuid === mainPublisherUuid);
      if (hostData) {
        processedHost = {
          id: hostData.userId, // Use userId as primary ID
          uuid: hostData.uuid, // Keep uuid
          name: hostData.userName,
          avatar: hostData?.image || fallbackAvatar,
          isActive: true, // Başlangıç varsayılanları
          isMuted: false, // Başlangıç varsayılanları
          level: hostData.userBadgeLevel === 'boho_member' ? 10 : null, // Örnek seviye eşleme
          agoraUid: hostData.agoraUid,
        }
      } else {
        console.warn('[VoiceRoomService] Main host not found in publishers list.')
      }

      // Diğer speaker'ları filtrele ve işle (userId ile filtrele, id olarak userId ata)
      const identifiedHostUserId = hostData ? hostData.userId : null
      processedSpeakers = publishers
        .filter(p => p.userId !== identifiedHostUserId)
        .map(speakerData => ({
          id: speakerData.userId, // Use userId as primary ID
          uuid: speakerData.uuid, // Keep uuid
          name: speakerData.userName,
          avatar: speakerData?.image || fallbackAvatar,
          isActive: true,
          isMuted: false,
          level: speakerData.userBadgeLevel === 'boho_member' ? 10 : null,
          agoraUid: speakerData.agoraUid,
        }))

      return { mainHost: processedHost, speakers: processedSpeakers }
    }

    console.error('[VoiceRoomService] Invalid response format from getVoiceRoomPublishers.')
    throw new Error('Konuşmacı listesi alınamadı (geçersiz format).')
  } catch (error) {
    console.error('[VoiceRoomService] Error fetching or processing publishers:', error)
    // Orijinal hatayı veya özel bir hata mesajını tekrar fırlatabiliriz
    throw new Error('Konuşmacı listesi alınırken sunucu hatası oluştu.')
  }
}

/**
 * Creates and initializes the Agora RTC client.
 * @param {Function} onUserPublished - Callback for 'user-published' event.
 * @param {Function} onUserUnpublished - Callback for 'user-unpublished' event.
 * @param {Function} onUserJoined - Callback for 'user-joined' event.
 * @param {Function} onUserLeft - Callback for 'user-left' event.
 * @returns {object} The initialized Agora RTC client instance.
 */
export function initializeAgoraClient(onUserPublished, onUserUnpublished, onUserJoined, onUserLeft) {
  const agoraClient = AgoraRTC.createClient({ mode: 'live', codec: 'vp8' });

  agoraClient.on('user-published', onUserPublished);
  agoraClient.on('user-unpublished', onUserUnpublished);
  agoraClient.on('user-joined', onUserJoined);
  agoraClient.on('user-left', onUserLeft);

  return agoraClient;
}

/**
 * Joins the Agora channel with the provided details.
 * @param {object} agoraClient - The initialized Agora RTC client.
 * @param {object} roomInfo - Contains channelName, rtcToken, etc.
 * @param {number|string} userIdentifier - The user ID for Agora.
 * @returns {Promise<number|string>} The UID assigned by Agora.
 * @throws Will throw an error if join fails.
 */
export async function joinAgoraChannel(agoraClient, roomInfo, userIdentifier) {
  const appId = process.env.VUE_APP_AGORA_ID;
  const { channelName, rtcToken } = roomInfo;

  if (!agoraClient || !appId || !channelName || !rtcToken || !userIdentifier) {
    console.error('[VoiceRoomService] Missing required parameters for joining Agora channel.', { appId, channelName, rtcToken, userIdentifier, clientExists: !!agoraClient });
    throw new Error('Agora kanalına katılmak için gerekli bilgiler eksik.');
  }

  try {
    const uid = await agoraClient.join(
      appId,
      channelName,
      rtcToken,
      userIdentifier
    );

    return uid;
  } catch (error) {
    console.error('[VoiceRoomService] Error joining Agora channel:', error);
    throw error; // Re-throw for the component to handle
  }
}

/**
 * Sets the client role in Agora.
 * @param {object} agoraClient - The Agora RTC client.
 * @param {'host'|'audience'} role - The desired role.
 * @returns {Promise<void>}
 * @throws Will throw an error if setting role fails.
 */
export async function setAgoraClientRole(agoraClient, role) {
  
  if (!agoraClient || (role !== 'host' && role !== 'audience')) {
      throw new Error('Invalid parameters for setting Agora role.');
  }
  try {
      await agoraClient.setClientRole(role);
      
  } catch (error) {
      console.error(`[VoiceRoomService] Error setting Agora client role to ${role}:`, error);
      throw error;
  }
}

/**
 * Creates and publishes the local microphone audio track.
 * @param {object} agoraClient - The Agora RTC client (must be in 'host' role).
 * @returns {Promise<object>} The created and published local audio track.
 * @throws Will throw an error if track creation or publish fails.
 */
export async function publishLocalAudioTrack(agoraClient) {
  
  if (!agoraClient) {
    throw new Error('Agora client is required to publish audio track.');
  }
  try {
    const localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack();
    
    await agoraClient.publish(localAudioTrack);
    
    return localAudioTrack;
  } catch (error) {
    console.error('[VoiceRoomService] Error creating or publishing local audio track:', error);
    throw error;
  }
}

/**
 * Cleans up Agora resources (audio track and client connection).
 * @param {object|null} agoraClient - The Agora RTC client instance.
 * @param {object|null} localAudioTrack - The local audio track instance.
 */
export async function leaveAgoraChannelService(agoraClient, localAudioTrack) {
  
  if (localAudioTrack) {
    try {
      localAudioTrack.close();
      
    } catch (trackCloseError) {
        console.error('[VoiceRoomService] Error closing local audio track:', trackCloseError);
    }
  }
  if (agoraClient) {
    try {
      await agoraClient.leave();
      
    } catch (leaveError) {
      console.error('[VoiceRoomService] Error leaving Agora channel via client:', leaveError);
      // Optionally re-throw or handle differently
    }
  }
}

/**
 * Ends the voice room stream by calling the backend API and leaving the Agora channel.
 * @param {object|null} agoraClient - The Agora RTC client instance.
 * @param {object|null} localAudioTrack - The local audio track instance.
 * @returns {Promise<boolean>} True if successful, false otherwise.
 */
export async function endStreamService(agoraClient, localAudioTrack) {
  
  try {
    // 1. Call the backend API to signal the end
    const response = await useJwt.endVoiceRoom();  

    // 2. Leave the Agora channel
    await leaveAgoraChannelService(agoraClient, localAudioTrack);

    return true; // Indicate success

  } catch (error) {
    console.error('[VoiceRoomService] Error during endStreamService:', error);

    return false; // Indicate failure
  }
}

/**
 * Handles an audience member leaving the Agora channel.
 * Only leaves Agora, does not call backend endVoiceRoom.
 * @param {object} agoraClient - The Agora RTC client instance.
 * @returns {Promise<boolean>} True if leaving Agora was successful, false otherwise.
 */
export async function leaveRoomAsAudienceService(agoraClient) {
  
  try {
    // Audience doesn't have a local audio track to close
    await leaveAgoraChannelService(agoraClient, null); // Pass null for localAudioTrack
    return true;
  } catch (error) {
    console.error('[VoiceRoomService] Error during leaveRoomAsAudienceService:', error);
    return false;
  }
}

/**
 * Sends a heartbeat request to the backend for the given channel.
 * @param {string} channelName - The name of the channel for the heartbeat.
 * @returns {Promise<object|null>} The body of the heartbeat response on success, null on error.
 */
export async function sendHeartbeatRequest(channelName) {
  if (!channelName) {
    console.warn('[VoiceRoomService] Skipping heartbeat: channelName not provided.');
    return null;
  }

  try {
    // Assuming useJwt is already imported in this file
    const response = await useJwt.sendVoiceRoomHeartbeat(); // Does this need channelName? API definition shows no args
    const responseBody = response?.data?.body;
    
    return responseBody || null; // Return body or null if body is empty/undefined
  } catch (error) {
    console.error('[VoiceRoomService] Error sending heartbeat:', error);
    return null; // Indicate failure by returning null
  }
}

/**
 * Kullanıcının konuşmacı olma isteğini API'ye gönderir.
 * @returns {Promise<boolean>} İstek başarılıysa true, değilse false döner veya hata fırlatır.
 */
export async function requestToBecomeSpeakerService() {

  try {
    // Check if useJwt itself exists and has the required method
    if (!useJwt || typeof useJwt.sendVoiceRoomBePublisher !== 'function') {
        console.error('[VoiceRoomService] useJwt instance or sendVoiceRoomBePublisher method is not available.', useJwt);
        throw new Error('JWT Service veya gerekli API metodu bulunamadı.');
    }

    // Doğrudan useJwt üzerinden çağrı yap
    const response = await useJwt.sendVoiceRoomBePublisher();

    // Başarı durumunu kontrol et (API'nin response yapısına göre değişebilir)
    // Örneğin, status 200 veya 204 ise başarılı kabul edilebilir.
    if (response && (response.status === 200 || response.status === 204 || response.data?.success)) {
      return true;
    } else {
      console.warn('[VoiceRoomService] Request to become speaker API call did not indicate success:', response);
      // Belki burada da bir hata fırlatmak daha iyi olur
      throw new Error('Konuşmacı olma isteği gönderildi ancak sunucu başarıyı onaylamadı.');
    }
  } catch (error) {
    console.error('[VoiceRoomService] Error requesting to become speaker:', error.response || error);
    // Hata mesajını tekrar fırlat
    const errorMessage = error.response?.data?.message || error.message || 'Konuşmacı olma isteği gönderilirken bir hata oluştu.';
    throw new Error(errorMessage);
  }
}

/**
 * Host'un bir konuşmacı isteğini onaylaması için API çağrısı yapar.
 * @param {string} userUuid Onaylanacak kullanıcının UUID'si.
 * @returns {Promise<boolean>} İstek başarılıysa true, değilse false döner veya hata fırlatır.
 */
export async function approveSpeakerRequestService(userUuid) {
  
  if (!userUuid) {
    console.error('[VoiceRoomService] Cannot approve request: userUuid is missing.');
    throw new Error('Onaylanacak kullanıcı kimliği eksik.');
  }

  try {
    
    // jwtService içindeki fonksiyonun adı ve aldığı parametreler doğru olmalı
    const response = await useJwt.approveSpeakerRequest(userUuid); // Doğrudan useJwt üzerinden

    // Başarı durumunu kontrol et
    if (response && (response.status === 200 || response.status === 204 || response.data?.success)) {
      
      return true;
    } else {
      console.warn('[VoiceRoomService] Approve speaker request API call did not indicate success:', response);
      throw new Error('Konuşmacı isteği onayı gönderildi ancak sunucu başarıyı onaylamadı.');
    }
  } catch (error) {
    console.error('[VoiceRoomService] Error approving speaker request:', error.response || error);
    const errorMessage = error.response?.data?.message || error.message || 'Konuşmacı isteği onaylanırken bir hata oluştu.';
    throw new Error(errorMessage);
  }
}

/**
 * Host'un bir konuşmacı isteğini reddetmesi için API çağrısı yapar.
 * @param {string} userUuid Reddedilecek kullanıcının UUID'si.
 * @returns {Promise<boolean>} İstek başarılıysa true, değilse false döner veya hata fırlatır.
 */
export async function rejectSpeakerRequestService(userUuid) {
  
  if (!userUuid) {
    console.error('[VoiceRoomService] Cannot reject request: userUuid is missing.');
    throw new Error('Reddedilecek kullanıcı kimliği eksik.');
  }

  try {
    
    // jwtService içindeki fonksiyonun adı ve aldığı parametreler doğru olmalı
    const response = await useJwt.rejectSpeakerRequest(userUuid); // Doğrudan useJwt üzerinden

    // Başarı durumunu kontrol et
    if (response && (response.status === 200 || response.status === 204 || response.data?.success)) {
      
      return true;
    } else {
      console.warn('[VoiceRoomService] Reject speaker request API call did not indicate success:', response);
      throw new Error('Konuşmacı isteği reddi gönderildi ancak sunucu başarıyı onaylamadı.');
    }
  } catch (error) {
    console.error('[VoiceRoomService] Error rejecting speaker request:', error.response || error);
    const errorMessage = error.response?.data?.message || error.message || 'Konuşmacı isteği reddedilirken bir hata oluştu.';
    throw new Error(errorMessage);
  }
}

/**
 * Handles the process of an audience member becoming a publisher (speaker).
 * Includes leaving the channel, rejoining with a new token, and publishing audio.
 * @param {object} agoraClient - The Agora RTC client instance.
 * @param {string} newToken - The new RTC token required for rejoining as a publisher.
 * @param {object} roomInfo - Current room information (channelName).
 * @param {number|string} userIdentifier - The user's identifier for Agora.
 * @returns {Promise<object|null>} The newly created local audio track, or null if failed.
 * @throws Will throw an error if any step fails.
 */
export async function becomePublisherService(agoraClient, newToken, roomInfo, userIdentifier) {
  

  if (!agoraClient || !newToken || !roomInfo || !userIdentifier) {
    console.error('[VoiceRoomService] Missing required parameters for becomePublisherService.');
    throw new Error('Konuşmacı olmak için gerekli bilgiler eksik.');
  }

  try {
    // 1. Leave the current channel
    
    await agoraClient.leave();
    
    // 2. Rejoin with the new token
    await joinAgoraChannel(agoraClient, { ...roomInfo, rtcToken: newToken }, userIdentifier);

    // 3. Set role to host (implicitly required before publishing)
    await setAgoraClientRole(agoraClient, 'host');

    // 4. Publish the local audio track
    const newLocalAudioTrack = await publishLocalAudioTrack(agoraClient);
    return newLocalAudioTrack;

  } catch (error) {
    console.error('[VoiceRoomService] Error during becomePublisherService:', error);
    // Attempt to revert to audience role on failure?
    try {
      console.warn('[VoiceRoomService] Attempting to set role back to audience after failure.');
      await setAgoraClientRole(agoraClient, 'audience');
    } catch (revertError) {
      console.error('[VoiceRoomService] Failed to revert role to audience:', revertError);
    }
    throw error; // Re-throw the original error for the component
  }
}

// --- Participant List Fetching ---

/**
 * Fetches the list of participants (publishers and subscribers) for a given voice room.
 * @param {string|number} channelId - The ID of the voice room channel.
 * @returns {Promise<object>} The API response body containing mainPublisherId, publishers, and subscribers.
 * @throws Will throw an error if the API call fails or response is invalid.
 */
export async function getListOfVoiceRoomParticipants(channelId) {
  
  if (!channelId) {
    console.error('[VoiceRoomService] Cannot fetch participants: channelId is missing.');
    throw new Error('Katılımcı listesini almak için kanal kimliği eksik.');
  }

  try {
    
    const response = await useJwt.getListOfVoiceRoomParticipants(channelId); // Pass channelId if needed by the API

    // Validate response structure
    if (response && response.data && response.data.body && Array.isArray(response.data.body.publishers) && Array.isArray(response.data.body.subscribers)) {
      
      return response.data.body; // Return the main body containing publishers and subscribers
    } else {
      console.warn('[VoiceRoomService] Fetched participants but response format is invalid:', response.data);
      return [];
    }
  } catch (error) {
    console.error('[VoiceRoomService] Error fetching participants:', error.response || error);
    const errorMessage = error.response?.data?.message || error.message || 'Katılımcı listesi alınırken bir hata oluştu.';
    throw new Error(errorMessage);
  }
}

export const getVoiceRoomGoalsService = async (channelId) => {
  if (!channelId) {
    console.error('[VoiceRoomService] channelId is required to fetch goals.');
    throw new Error('Channel ID is missing.');
  }
  try {
    const response = await useJwt.getGoalsOfVoiceRoom(channelId);
    
    // Check if the response and data exist and have the expected structure
    if (response && response.data.body && typeof response.data.body.streamTitle !== 'undefined' && Array.isArray(response.data.body.goals)) {
      return response.data.body; // Return the { streamTitle, goals } object
    } else {
      console.warn('[VoiceRoomService] Invalid or unexpected response structure for getGoalsOfVoiceRoom:', response);
      // Return a default structure or null to indicate failure without throwing
      return { streamTitle: 'Başlık Yüklenemedi', goals: [] }; 
    }
  } catch (error) {
    console.error('[VoiceRoomService] Error fetching voice room goals:', error);
    // Optionally map specific errors or re-throw
    const errorMessage = error.response?.data?.message || error.message || 'Oda hedefleri alınırken bir hata oluştu.';
    throw new Error(errorMessage); // Re-throwing for the action to handle if needed
  }
};

/**
 * Kicks a user from the voice room using their UUID.
 * Only hosts should be able to call this.
 * @param {string} userUuid - The UUID of the user to kick.
 * @returns {Promise<boolean>} - True if the request was likely successful, false otherwise.
 */
export const kickUserService = async (userUuid) => {
  if (!userUuid) {
    console.error('[Service - kickUserService] userUuid is required.');
    throw new Error('Kicklenecek kullanıcının UUID bilgisi eksik.'); // Or return false
  }
  
  try {
    // Assuming useJwt is correctly configured and has kickUserFromVoiceRoom method
    const response = await useJwt.kickUserFromVoiceRoom(userUuid);
    return true; // Indicate success
  } catch (error) {
    console.error(`[Service - kickUserService] Error kicking user ${userUuid}:`, error);
    // Rethrow or handle the error, maybe return false
    // throw new Error(`Kullanıcı (${userUuid}) kicklenirken hata: ${error.message || 'Bilinmeyen sunucu hatası'}`);
    return false; // Indicate failure
  }
};

/**
 * Toggles the mute status of a remote user in a voice room via backend API.
 * Should only be callable by the host.
 * @param {string|number} channelId - The ID of the voice room channel.
 * @param {string} targetUserUuid - The UUID of the user whose mute state to toggle.
 * @returns {Promise<boolean>} - True if the request was likely successful, false otherwise.
 */
export const toggleRemoteMuteService = async (channelId, targetUserUuid) => {
  if (!channelId || !targetUserUuid) {
    console.error('[Service - toggleRemoteMuteService] channelId and targetUserUuid are required.');
    throw new Error('Mute durumu değiştirilecek oda veya kullanıcı bilgisi eksik.');
  }
  
  try {
    // Call the method added to useJwt
    const response = await useJwt.toggleMuteVoiceRoom(channelId, targetUserUuid);
    
    // Adjust success check based on actual API response
    if (response && (response.status === 200 || response.data?.success)) {
         
         return true; // Indicate success
    } else {
         console.warn(`[Service - toggleRemoteMuteService] Toggle mute API call did not indicate success for ${targetUserUuid}.`, response);
         return false;
    }
   
  } catch (error) {
    console.error(`[Service - toggleRemoteMuteService] Error toggling mute for user ${targetUserUuid}:`, error.response?.data || error.message || error);
    // Rethrow or return false
    // throw new Error(`Kullanıcının (${targetUserUuid}) mute durumu değiştirilirken hata: ${error.message || 'Bilinmeyen sunucu hatası'}`);
    return false; // Indicate failure
  }
};

/**
 * Retrieves the list of users who sent gifts in a voice room.
 * @param {string|number} channelId - The ID of the voice room channel.
 * @returns {Promise<Array<object>>} A promise that resolves to an array of gift sender objects.
 * @throws Will throw an error if the API call fails.
 */
export async function getGiftSendersListService(channelId) {
  
  if (!channelId) {
    throw new Error('Channel ID is required to fetch gift senders.');
  }
  try {
    // Call the correct endpoint function from useJwt, passing the primitive channelId directly
    const response = await useJwt.getReceivedGiftsVoiceRoom(channelId); 
    
    // Assuming the list is directly in response.data or response.data.body
    if (response && response.data) {
        // Check if the actual data is nested within a 'body' property
        if (Array.isArray(response.data.body)) {
            return response.data.body;
        }
        // Check if the data is directly an array
        if (Array.isArray(response.data)) {
            return response.data;
        }
    }
    console.warn('[VoiceRoomService] Invalid or empty response format for gift senders:', response.data);
    return []; // Return empty array for invalid format or no data
  } catch (error) {
    console.error(`[VoiceRoomService] Error fetching gift senders list for channel ${channelId}:`, error);
    // Re-throw the error for the action to handle
    throw error;
  }
}

/**
 * Fetches the list of available gifts for the voice room.
 * @returns {Promise<Array<object>>} A list of gift objects.
 * @throws Will throw an error if fetching or processing fails.
 */
export async function getGifts() {
  
  try {
    const response = await useJwt.getVoiceRoomGifts();

    if (response && response.data && Array.isArray(response.data.body)) {
      const gifts = response.data.body.map(gift => ({
        productCode: gift.productCode,
        productId: gift.productId,
        label: gift.label,
        credits: gift.credits,
        image: gift.image,
        riv: gift.riv // Assuming riv is part of the gift data
      }));
      
      return gifts;
    } else {
      console.error('[VoiceRoomService] Invalid response format from getVoiceRoomGifts.'); // Keep error log
      throw new Error('Hediye listesi alınamadı (geçersiz format).');
    }
  } catch (error) {
    console.error('[VoiceRoomService] Error fetching or processing gifts:', error); // Keep error log
    // Re-throw the error or a custom one
    throw new Error('Hediye listesi alınırken sunucu hatası oluştu.');
  }
}

/**
 * Sends a gift in the voice room.
 * @param {object} giftData - Data for the gift to send (e.g., { productCode }).
 * @param t
 * @returns {Promise<object>} The response body from the API.
 * @throws Will throw an error if sending fails.
 */
export async function sendVoiceRoomGift(giftData) {
  if (!giftData || !giftData.productCode) {
    throw new Error(i18n.t('The gift sending process failed'))
  }

  try {
    const response = await useJwt.sendVoiceRoomGift(giftData)

    // Check for successful response if necessary based on API design
    if (response && response.data) { // Basic check, adjust as needed
      const responseBody = response.data.body

      console.log('responseBody', responseBody)

      if (typeof responseBody.success !== 'undefined' && responseBody.success) {
        return {
          result: true,
        }
      }
      console.log('responseBody', responseBody)
      return {
        result: false,
        error: i18n.t('You don\'t have enough credit. Please go to the store and purchase credit.'),
      }
    } else {
      console.log('responseBody else')
      return {
        result: false,
        error: i18n.t('The gift sending process failed'),
      }
    }
  } catch (error) {
    console.log('responseBody catch', error)
    // Re-throw the error or a custom one
    return {
      result: false,
      error,
    } // Let the component handle the error details
  }
}

/**
 * Subscribes the current user (as audience) to a voice room.
 * @param {string|number} channelId - The ID of the channel to join.
 * @returns {Promise<object>} A promise that resolves to the roomInfo object needed for the store.
 * @throws Will throw an error if the API call fails or returns an error code.
 */
export async function subscribeVoiceRoom(channelId) {
  console.log(`[VoiceRoomService] Subscribing to channel ${channelId}`)
  if (!channelId) {
    throw new Error('Channel ID is required to subscribe.')
  }

  try {
    const response = await useJwt.subscribeVoiceRoom(channelId)
    console.log('[VoiceRoomService] subscribeVoiceRoom response:', response)

    // Check if the expected body exists (Only process if API call didn't throw error)
    if (response?.data?.body) {
      const apiData = response.data.body
      const roomInfo = {
        channelId: apiData.channelId,
        channelName: apiData.channelName,
        agoraUid: apiData.agoraUid, // This is the SUBSCRIBER's Agora UID
        rtcToken: apiData.rtcToken,
        heartbeatIntervalInSecs: apiData.heartbeatIntervalInSecs,
        mainPublisherUuid: apiData.mainPublisherUuid,
        image: apiData.image,
        // Include any other necessary fields from apiData
        isHost: false, // Always false when subscribing this way
        // Add other fields returned by API if needed (premiumRoom, privateRoom etc.)
        premiumRoom: apiData.premiumRoom,
        privateRoom: apiData.privateRoom,
        psrInfo: apiData.psrInfo, // Include psrInfo if applicable
      }
      console.log('[VoiceRoomService] Processed roomInfo for subscriber:', roomInfo)
      return roomInfo
    }
    // Handle cases where response is successful (2xx) but body is missing/invalid
    console.error('[VoiceRoomService] Invalid response body from subscribeVoiceRoom:', response.data)
    throw new Error('Ses odasına katılırken geçersiz yanıt alındı.')
  } catch (error) {
    // Handle network errors or errors thrown from the API call
    console.error('[VoiceRoomService] Error in subscribeVoiceRoom:', error)

    // Check if it's an API error with the specific header structure
    if (error?.response?.data?.header?.error?.errorCode) {
      const errorInfo = error.response.data.header.error
      console.error('[VoiceRoomService] API returned an error on subscribe:', errorInfo)
      // Throw a new Error with the specific message from the API
      throw new Error(errorInfo.message || 'Ses odasına katılırken API\'den bir hata alındı.')
    }

    // For other errors (network, unexpected structure, etc.), re-throw or create a generic error
    // If error already has a message (like Axios default), use it, otherwise provide a default
    throw new Error(error?.message || 'Ses odasına katılırken beklenmedik bir sorun oluştu.')
  }
}
